import { each, filter, find, map, pickBy, uniq } from 'lodash-es';
/**
 * The type of order which is deduced based on line action types present
 * and possibly other characteristics
 *
 * @export
 * @enum {number}
 */
export var OrderMode;
(function (OrderMode) {
    OrderMode["empty"] = "EMPTY";
    OrderMode["sale"] = "SALE";
    OrderMode["reservation"] = "RESERVATION";
    OrderMode["delivery"] = "DELIVERY";
    OrderMode["order"] = "ORDER";
    OrderMode["interbranch"] = "INTERBRANCH";
    OrderMode["returnToSupplier"] = "RETURN_TO_SUPPLIER";
    OrderMode["mixed"] = "MIXED";
})(OrderMode || (OrderMode = {}));
/**
 * Helper method to determine if an order has product with product requirements
 * based on the getRequiredDataForOrder response
 *
 * @export
 * @param {{
 *   requiredData: EVA.Core.GetRequiredDataForOrder,
 * }} {
 *   requiredData,
 * }
 * @returns {boolean}
 */
export function hasProductRequirement({ requiredData, }) {
    const orderLineRequirements = requiredData.OrderLines;
    const hasProductRequirement = !!find(orderLineRequirements, (orderLineRequirementsForOrderLine) => {
        return !!find(orderLineRequirementsForOrderLine, { Name: 'ProductRequirement' });
    });
    return hasProductRequirement;
}
/**
 * Helper method to determine the current order mode
 *
 * @export
 * @param {{
 *   order: EVA.Core.OrderDto,
 * }} {
 *   order,
 * }
 * @returns
 */
export function determineOrderMode({ order, }) {
    // Collect the cart order line types currently in use
    //
    // None = 0,
    // ReserveLine = 1,
    // OrderLine = 2,
    // ShipLine = 3,
    // Delivery = 4,
    //
    // The None type will be omitted
    //
    const activeLines = filter(order.Lines, (line) => !line.IsCancelled);
    const lineActionTypesInCart = uniq(filter(map(activeLines, 'LineActionType'), (lineActionType) => lineActionType !== 0));
    // Determine the order mode
    //
    if ((order.Characteristics & 4) !== 0) {
        return OrderMode.interbranch;
    }
    if ((order.Characteristics & 128) !== 0) {
        return OrderMode.returnToSupplier;
    }
    if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 1) {
        return OrderMode.reservation;
    }
    if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 2) {
        return OrderMode.order;
    }
    if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 3) {
        return OrderMode.sale;
    }
    if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 4) {
        return OrderMode.delivery;
    }
    if (lineActionTypesInCart.length > 1) {
        return OrderMode.mixed;
    }
    return OrderMode.empty;
}
/**
 * Helper method to calculate the current order (checkout) status based
 * on the order and required data response
 *
 * @export
 * @param {{
 *     order: EVA.Core.OrderDto,
 *     requiredData: EVA.Core.GetRequiredDataForOrderResponse,
 *   }} {
 *     order,
 *     requiredData,
 *   }
 * @returns {IOrderStatus}
 */
export function determineOrderStatus({ order, requiredData, productRequirements, }) {
    const orderLines = order.Lines;
    // Find all the current issues from the required data response
    //
    const customerRequirements = requiredData.Customer;
    const customerIssues = filter(customerRequirements, { IsValid: false });
    const orderRequirements = requiredData.Order;
    const orderIssues = filter(orderRequirements, { IsValid: false });
    const orderLineRequirements = requiredData.OrderLines;
    const orderLineIssues = pickBy(orderLineRequirements, (orderLineRequirements) => {
        return !!find(orderLineRequirements, { IsValid: false });
    });
    const invalidOrderLineIDs = map(Object.keys(orderLineIssues), key => parseInt(`${key}`, 10));
    // We will provide some helpers to deal with specific requirements
    // for bundles, serial numbers and product with required fields
    //
    const bundleProducts = [];
    const pickDiscountProducts = [];
    const productsWithRequirements = [];
    const productsWithSerialNumbers = [];
    each(orderLineRequirements, (orderLineRequirementsForOrderLine, orderLineID) => {
        each(orderLineRequirementsForOrderLine, (orderLineRequirement) => {
            switch (orderLineRequirement.Name) {
                case 'CompleteProductBundle':
                    {
                        const bundleProductSubProducts = filter(orderLines, orderLine => `${orderLine.ParentID}` === `${orderLineID}`);
                        bundleProducts.push({
                            orderLineID: parseInt(orderLineID, 10),
                            requirement: orderLineRequirement,
                            valid: orderLineRequirement.IsValid,
                            productLineIDs: map(bundleProductSubProducts, 'ID'),
                        });
                    }
                    break;
                case 'ProductRequirement':
                    {
                        const productRequirementsForOrderLine = find(productRequirements, { OrderLineID: parseInt(orderLineID, 10) });
                        productsWithRequirements.push({
                            orderLineID: parseInt(orderLineID, 10),
                            requirement: orderLineRequirement,
                            valid: orderLineRequirement.IsValid,
                            values: productRequirementsForOrderLine?.RequirementModels || [],
                        });
                    }
                    break;
                case 'SerialNumber':
                    {
                        const productWithSerialNumber = find(orderLines, orderLine => `${orderLine.ID}` === `${orderLineID}`);
                        productsWithSerialNumbers.push({
                            orderLineID: parseInt(orderLineID, 10),
                            requirement: orderLineRequirement,
                            valid: orderLineRequirement.IsValid,
                            serialNumber: productWithSerialNumber?.SerialNumber,
                        });
                    }
                    break;
                case 'PickDiscountProduct':
                    {
                        // The relation between the discount line and the products picked
                        // for the discount is that the discount has the product line as its parent
                        //
                        const discountLine = find(orderLines, orderLine => `${orderLine.ID}` === `${orderLineID}`);
                        const pickedDiscountProducts = filter(orderLines, orderLine => `${orderLine.ID}` === `${discountLine?.ParentID}`);
                        pickDiscountProducts.push({
                            orderLineID: parseInt(orderLineID, 10),
                            requirement: orderLineRequirement,
                            valid: orderLineRequirement.IsValid,
                            pickedProductLineIDs: map(pickedDiscountProducts, 'ID'),
                        });
                    }
                    break;
            }
        });
    });
    // Default step status when no information is available
    //
    const orderMode = determineOrderMode({ order });
    const lineActionTypes = uniq(filter(map(order.Lines, 'LineActionType'), (lineActionType) => lineActionType !== 0));
    const status = {
        orderMode,
        lineActionTypes,
        bundleProducts,
        pickDiscountProducts,
        productsWithRequirements,
        productsWithSerialNumbers,
        issues: {
            invalidOrderLineIDs,
            customer: customerIssues,
            order: orderIssues,
            orderLine: orderLineIssues,
        },
        checkoutSteps: {
            attachCustomer: {
                valid: true,
                possible: false,
                required: false,
            },
            createOrder: {
                valid: true,
                possible: false,
                required: false,
            },
            payment: {
                valid: true,
                possible: false,
                required: false,
            },
            placeOrder: {
                valid: true,
                possible: false,
                required: false,
            },
            shipOrder: {
                valid: true,
                possible: false,
                required: false,
            },
        },
    };
    //////////////////////////////////////////////////////////////////////
    // Step: attachCustomer (order level requirement)
    //////////////////////////////////////////////////////////////////////
    //
    const attachCustomerOrderRequirement = find(orderRequirements, { Name: 'Customer' });
    if (attachCustomerOrderRequirement) {
        status.checkoutSteps.attachCustomer = {
            possible: true,
            required: true,
            valid: attachCustomerOrderRequirement.IsValid && customerIssues.length === 0,
        };
    }
    //////////////////////////////////////////////////////////////////////
    // Step: createOrder (order level requirement)
    //////////////////////////////////////////////////////////////////////
    //
    // This is fulfilled by calling `EVA.Core.PrepareOrderForCheckout`
    const createOrderRequirement = find(orderRequirements, { Name: 'CreateOrder' });
    if (createOrderRequirement) {
        status.checkoutSteps.createOrder = {
            possible: true,
            required: !createOrderRequirement.IsValid,
            valid: createOrderRequirement.IsValid,
        };
    }
    //////////////////////////////////////////////////////////////////////
    // Step: payOrder (all levels based on required for)
    //////////////////////////////////////////////////////////////////////
    //
    // Check current issues to determine if possible
    //
    // PlaceOrder = 1,
    // Payment = 2,
    // Ship = 4,
    // Invoice = 8,
    // All = 15,
    //
    const payOrderRequirement = find(orderRequirements, { Name: 'PayOrder' });
    if (payOrderRequirement) {
        const noCustomerIssues = !find(customerIssues, issue => (issue.RequiredFor & 2) === 2);
        const noOrderIssues = !find(orderIssues, issue => (issue.RequiredFor & 2) === 2);
        const noOrderLineIssues = !find(orderLineIssues, (lineIssues) => {
            return !!find(lineIssues, issue => !issue.IsValid && (issue.RequiredFor & 2) === 2);
        });
        /** If its a delivery order, payment is not required */
        const isDeliverMode = orderMode === OrderMode.delivery;
        /** If its an order, payment is not required */
        const isOrderMode = orderMode === OrderMode.order;
        /** If its a reservation order, payment is not required */
        const isReservationMode = orderMode === OrderMode.reservation;
        const payOrderStepRequired = !isDeliverMode && !isOrderMode && !isReservationMode;
        status.checkoutSteps.payment = {
            possible: noCustomerIssues && noOrderIssues && noOrderLineIssues,
            required: payOrderStepRequired,
            valid: payOrderRequirement.IsValid,
        };
    }
    //////////////////////////////////////////////////////////////////////
    // Step: placeOrder (all levels based on required for)
    //////////////////////////////////////////////////////////////////////
    //
    // Check current issues to determine if possible
    //
    // PlaceOrder = 1,
    // Payment = 2,
    // Ship = 4,
    // Invoice = 8,
    // All = 15,
    //
    const placeOrderRequirement = find(orderRequirements, { Name: 'PlaceOrder' });
    if (placeOrderRequirement) {
        // For place order we need to exclude the `PlaceOrder` requirement itself
        // The RequiredFor level for the `PlaceOrder` was ship (4) in the past but it
        // has been lowered to place (1) due to a placement flow interaction with
        // RMA orders
        //
        const noCustomerIssues = !find(customerIssues, issue => (issue.RequiredFor & 1) === 1);
        const noOrderIssues = !find(orderIssues, issue => (issue.RequiredFor & 1) === 1 && issue.Name !== 'PlaceOrder');
        const noOrderIssuesAfterPayment = !find(orderIssues, issue => (issue.RequiredFor & 1) === 1 && issue.Name !== 'PlaceOrder' && issue.Name !== 'PayOrder');
        const noOrderLineIssues = !find(orderLineIssues, (lineIssues) => {
            return !!find(lineIssues, issue => !issue.IsValid && (issue.RequiredFor & 1) === 1);
        });
        status.checkoutSteps.placeOrder = {
            possible: noCustomerIssues && noOrderIssues && noOrderLineIssues,
            required: true,
            valid: placeOrderRequirement.IsValid,
            possibleAfterPayment: noCustomerIssues && noOrderIssuesAfterPayment && noOrderLineIssues,
        };
    }
    //////////////////////////////////////////////////////////////////////
    // Step: ShipOrder (all levels based on required for and order content)
    //////////////////////////////////////////////////////////////////////
    //
    // There is no 'ShipOrder' requirement but if any lines are of type ship
    // then the order should be shipped
    // The IsShipped property on the order indicates completion
    //
    // The presence of RestrictedShippingAddress on either order or order line
    // makes the order un-shippable
    //
    // Check current issues to determine if possible
    // Possible needs to start as true for this logic to work
    //
    // PlaceOrder = 1,
    // Payment = 2,
    // Ship = 4,
    // Invoice = 8,
    // All = 15,
    //
    // For orders with only LineActionType 0 we want to call the shipOrder method.
    // This was added for the returning of fashion cheques flow in the POS
    //
    const onlyHasLineActionTypeZero = filter(map(order.Lines, 'LineActionType'), (lineActionType) => lineActionType === 0).length > 0;
    const hasShippingLines = !!find(orderLines, { LineActionType: 3 });
    if (hasShippingLines || onlyHasLineActionTypeZero) {
        const noCustomerIssues = !find(customerIssues, issue => (issue.RequiredFor & 4) === 4);
        const noOrderIssues = !find(orderIssues, issue => (issue.RequiredFor & 4) === 4);
        const noOrderLineIssues = !find(orderLineIssues, (lineIssues, orderLineID) => {
            // Check the order line is of type ship (3)
            //
            const issueLine = find(orderLines, orderLine => `${orderLine.ID}` === `${orderLineID}`);
            return issueLine?.LineActionType === 3 && !!find(lineIssues, issue => !issue.IsValid && (issue.RequiredFor & 4) === 4);
        });
        const orderRestrictedForShipping = !!find(orderIssues, { Name: 'RestrictedShippingAddress' });
        const orderLineRestrictedForShipping = find(orderLineIssues, lineIssues => !!find(lineIssues, { Name: 'RestrictedShippingAddress' }));
        status.checkoutSteps.shipOrder = {
            possible: noCustomerIssues && noOrderIssues && noOrderLineIssues,
            required: true,
            valid: !orderRestrictedForShipping && !orderLineRestrictedForShipping && order.IsShipped,
        };
    }
    return status;
}
//# sourceMappingURL=order-status.js.map